import React, {
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
} from "react";
import { Spinner } from "@chakra-ui/react";
import { PutWordStatusRequest, UserWord, UserWordStatus } from "@shared_types";
import { callWithAuth, putWordStatus, deleteUserWord } from "../../api/Api";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useUserWords } from "@/hooks/useUserWords";
import WordCard from "./WordCard";
import SegmentedViewHeader from "./SegmentedViewHeader";

interface SegmentedWordsViewProps {
  onClose: () => void;
  // should be called on deletes or known / learning status changes
  onStatusUpdate: () => void;
}

type ViewMode = "known" | "learning";

const PAGE_SIZE = 20;
const DEBOUNCE_TIME_MS = 300;

const SegmentedWordsView = React.forwardRef<
  { refreshQueries: () => void },
  SegmentedWordsViewProps
>(({ onClose, onStatusUpdate }, ref) => {
  const [viewMode, setViewMode] = useState<ViewMode>("learning");
  const [deleteConfirmId, setDeleteConfirmId] = useState<
    string | number | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingRefetch, setPendingRefetch] = useState(false);
  // Track modified words to update their status visually
  const [modifiedWords, setModifiedWords] = useState<
    Record<number, UserWordStatus>
  >({});

  // Refs for infinite scrolling
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastWordElementRef = useRef<HTMLDivElement | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_TIME_MS);
  const queryClient = useQueryClient();

  const {
    activeQuery,
    activeViewWords,
    knownCount,
    learningCount,
    fetchNextPage,
    refetchAllQueries,
    isInitialLoading: isLoading,
    isLoadingMore: isFetchingNextPage,
  } = useUserWords({
    viewMode,
    searchQuery: debouncedSearchQuery,
    pageSize: PAGE_SIZE,
  });

  // Implement refreshQueries function for the ref
  useImperativeHandle(ref, () => ({
    refreshQueries: () => {
      handleRefetchQueries();
    },
  }));

  const handleRefetchQueries = () => {
    setPendingRefetch(false);
    refetchAllQueries();
  };

  // Custom close handler that checks if refetch is needed
  const handleClose = () => {
    if (pendingRefetch) {
      handleRefetchQueries();
      // Clear modified words tracking
      setModifiedWords({});
    }
    onClose();
  };

  // Custom view mode change handler that checks if refetch is needed
  const handleViewModeChange = (newMode: ViewMode) => {
    if (pendingRefetch) {
      handleRefetchQueries();
      // Clear modified words tracking
      setModifiedWords({});
    }
    setViewMode(newMode);
  };

  // Intersection observer for infinite scrolling
  const lastWordRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();

      lastWordElementRef.current = node;

      if (!node) return;

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
          if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
            activeQuery.fetchNextPage();
          }
        }
      });

      observerRef.current.observe(node);
    },
    [activeQuery, fetchNextPage]
  );

  // Switch between server-side and client-side search based on query length
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleWordStatusChange = async (
    word: UserWord,
    newStatus: UserWordStatus
  ) => {
    const data: PutWordStatusRequest = {
      wordKey: word.wordKey,
      newStatus: newStatus,
      definitionId: word.translationId,
      lessonId: 0,
    };

    try {
      // Update the modified words map to track this change
      setModifiedWords((prev) => ({
        ...prev,
        [word.userWordId]: newStatus,
      }));
      await callWithAuth(putWordStatus, data);
      // Mark that a refetch is needed, but don't do it yet
      setPendingRefetch(true);
      // Update the stats count immediately
      queryClient.setQueryData(["dictionaryStats"], (oldData: any) => {
        if (!oldData) return oldData;
        const newData = { ...oldData };

        if (newStatus === "known") {
          newData.knownWordCount = (oldData.knownWordCount || 0) + 1;
          newData.learningWordCount = Math.max(
            0,
            (oldData.learningWordCount || 0) - 1
          );
        } else {
          newData.knownWordCount = Math.max(
            0,
            (oldData.knownWordCount || 0) - 1
          );
          newData.learningWordCount = (oldData.learningWordCount || 0) + 1;
        }

        return newData;
      });
      // Notify parent component about the status change
      onStatusUpdate();
    } catch (error) {
      console.error(error);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = async (wordId: number) => {
    if (deleteConfirmId === wordId) {
      try {
        await callWithAuth(deleteUserWord, {
          userWordId: wordId,
        });
        // For deletes, immediately refetch the data
        activeQuery.refetch();
        // Remove this word from our modified words tracking
        setModifiedWords((prev) => {
          const newModified = { ...prev };
          delete newModified[wordId];
          return newModified;
        });

        // Notify parent component
        onStatusUpdate();

        // Reset confirmation state
        setDeleteConfirmId(null);
      } catch (error) {
        console.error(error);
      }
    } else {
      // First click - set confirmation state
      setDeleteConfirmId(wordId);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  const renderEmptyState = () => {
    const message =
      viewMode === "known" ? "No known words yet" : "No learning words yet";

    const subMessage =
      viewMode === "known"
        ? "Mark words as known to see them here"
        : "Mark words as learning to see them here";

    return (
      <div className="flex flex-col items-center justify-center w-full h-64 p-8">
        <div className="text-xl font-semibold text-gray-400">{message}</div>
        <div className="text-base font-normal text-gray-400">{subMessage}</div>
      </div>
    );
  };

  // Get actual words to display
  const deduplicatedWords = deduplicateByWordKey(activeViewWords);

  return (
    <div className="flex flex-col w-full">
      <SegmentedViewHeader
        title="Words"
        titleColor="var(--color-blue)"
        viewMode={viewMode}
        knownCount={knownCount}
        learningCount={learningCount}
        searchQuery={searchQuery}
        onViewModeChange={handleViewModeChange}
        onSearchChange={handleSearchChange}
        onClose={handleClose}
        searchPlaceholder="Search words..."
      />

      <div className="min-h-[50vh]">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner
              size="xl"
              color={
                viewMode === "known"
                  ? "var(--color-blue)"
                  : "var(--color-orange)"
              }
            />
          </div>
        ) : deduplicatedWords.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className="flex flex-col gap-4">
            {deduplicatedWords.map((word, index) => {
              // Check if this word has been modified and use that status if available
              const effectiveStatus =
                modifiedWords[word.userWordId] || word.status;

              return (
                <WordCard
                  viewType={viewMode}
                  key={word.userWordId.toString()}
                  word={word}
                  effectiveStatus={effectiveStatus}
                  deleteConfirmId={deleteConfirmId}
                  onStatusChange={handleWordStatusChange}
                  onDeleteClick={handleDeleteClick}
                  onCancelDelete={handleCancelDelete}
                  lastWordRef={
                    index === deduplicatedWords.length - 10 ? lastWordRef : null
                  }
                />
              );
            })}

            {/* Loading indicator for fetching next page */}
            {isFetchingNextPage && (
              <div className="flex justify-center my-4">
                <Spinner
                  size="md"
                  color={
                    viewMode === "known"
                      ? "var(--color-blue)"
                      : "var(--color-orange)"
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

function deduplicateByWordKey(words: UserWord[]) {
  const seen: Record<string, boolean> = {};
  return words.filter((word) => {
    if (seen[word.wordKey]) {
      return false;
    }
    seen[word.wordKey] = true;
    return true;
  });
}

export default SegmentedWordsView;
